import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Calculator, Download, RefreshCw, Save, ShieldBan } from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useAdminAuth } from '../context/AdminAuthContext';
import { getErrorMessage } from '../../lib/utils';

const categories = [
  { id: 'quotation_value', label: 'Quotation Value' },
  { id: 'freight_weight', label: 'Freight Weight' },
  { id: 'response_time', label: 'Average Response Time' },
];
const periods = [
  { id: 'weekly', label: 'Weekly' },
  { id: 'monthly', label: 'Monthly' },
  { id: 'yearly', label: 'Yearly' },
  { id: 'all_time', label: 'All Time' },
];

const AdminLeaderboard = () => {
  const { api, hasPermission } = useAdminAuth();
  const canEdit = hasPermission('settings', 'edit');
  const [category, setCategory] = useState('quotation_value');
  const [period, setPeriod] = useState('monthly');
  const [activeTab, setActiveTab] = useState('rankings');
  const [leaderboard, setLeaderboard] = useState(null);
  const [settings, setSettings] = useState(null);
  const [flags, setFlags] = useState([]);
  const [exclusions, setExclusions] = useState([]);
  const [suspensions, setSuspensions] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [working, setWorking] = useState(false);
  const [exclusionForm, setExclusionForm] = useState({ record_type: 'inquiry', record_id: '', reason: '' });
  const [suspensionForm, setSuspensionForm] = useState({ company_id: '', reason: '' });
  const [details, setDetails] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  const loadRankings = useCallback(async () => {
    const response = await api.get('/api/admin/leaderboard', { params: { category, period } });
    setLeaderboard(response.data);
  }, [api, category, period]);

  const loadAdminData = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError('');
      const settingsResponse = await api.get('/api/admin/leaderboard/settings', { timeout: 10000 });
      setSettings(settingsResponse.data);

      const auxiliaryRequests = await Promise.allSettled([
        api.get('/api/admin/leaderboard/flags', { timeout: 10000 }),
        api.get('/api/admin/leaderboard/exclusions', { timeout: 10000 }),
        api.get('/api/admin/leaderboard/company-suspensions', { timeout: 10000 }),
        api.get('/api/admin/leaderboard/history', { params: { limit: 50 }, timeout: 10000 }),
      ]);
      const [flagsResponse, exclusionsResponse, suspensionsResponse, historyResponse] = auxiliaryRequests;
      if (flagsResponse.status === 'fulfilled') setFlags(flagsResponse.value.data || []);
      if (exclusionsResponse.status === 'fulfilled') setExclusions(exclusionsResponse.value.data || []);
      if (suspensionsResponse.status === 'fulfilled') setSuspensions(suspensionsResponse.value.data || []);
      if (historyResponse.status === 'fulfilled') setHistory(historyResponse.value.data || []);

      if (auxiliaryRequests.some((result) => result.status === 'rejected')) {
        toast.warning('Some leaderboard review data could not be loaded. Use Refresh to retry.');
      }
    } catch (error) {
      const message = getErrorMessage(error.response?.data?.detail, 'Failed to load leaderboard administration');
      setLoadError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    loadAdminData();
  }, [loadAdminData]);

  useEffect(() => {
    loadRankings().catch((error) => toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to load rankings')));
  }, [loadRankings]);

  const selectedCategory = useMemo(() => categories.find((item) => item.id === category), [category]);

  const metric = (row) => {
    if (category === 'quotation_value') return `${leaderboard?.standard_currency || 'USD'} ${Number(row.quotation_value || 0).toLocaleString()}`;
    if (category === 'freight_weight') return `${Number(row.freight_weight || 0).toLocaleString()} kg`;
    return row.qualified ? row.response_display : 'Not enough activity';
  };

  const saveSettings = async () => {
    try {
      setWorking(true);
      const response = await api.put('/api/admin/leaderboard/settings', settings);
      setSettings(response.data);
      toast.success('Leaderboard settings saved');
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to save settings'));
    } finally {
      setWorking(false);
    }
  };

  const recalculate = async () => {
    try {
      setWorking(true);
      await api.post('/api/admin/leaderboard/recalculate');
      await Promise.all([loadAdminData(), loadRankings()]);
      toast.success('All leaderboard periods recalculated');
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Recalculation failed'));
    } finally {
      setWorking(false);
    }
  };

  const exportCsv = async () => {
    try {
      const response = await api.get('/api/admin/leaderboard/export', {
        params: { category, period },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `leaderboard-${category}-${period}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to export leaderboard');
    }
  };

  const addExclusion = async () => {
    try {
      setWorking(true);
      await api.post('/api/admin/leaderboard/exclusions', exclusionForm);
      setExclusionForm({ record_type: 'inquiry', record_id: '', reason: '' });
      await loadAdminData();
      toast.success('Record excluded from leaderboard calculations');
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to exclude record'));
    } finally {
      setWorking(false);
    }
  };

  const restoreExclusion = async (row) => {
    try {
      await api.delete(`/api/admin/leaderboard/exclusions/${row.record_type}/${encodeURIComponent(row.record_id)}`);
      await loadAdminData();
      toast.success('Record restored');
    } catch (error) {
      toast.error('Failed to restore record');
    }
  };

  const suspendCompany = async () => {
    try {
      setWorking(true);
      await api.post('/api/admin/leaderboard/company-suspension', { ...suspensionForm, suspended: true });
      setSuspensionForm({ company_id: '', reason: '' });
      await loadAdminData();
      toast.success('Company suspended from leaderboard');
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to suspend company'));
    } finally {
      setWorking(false);
    }
  };

  const restoreCompany = async (row) => {
    try {
      await api.post('/api/admin/leaderboard/company-suspension', {
        company_id: row.company_id,
        suspended: false,
        reason: 'Leaderboard eligibility restored by administrator',
      });
      await loadAdminData();
      toast.success('Company leaderboard eligibility restored');
    } catch (error) {
      toast.error('Failed to restore company');
    }
  };

  const reviewFlag = async (row, status) => {
    try {
      await api.put(`/api/admin/leaderboard/flags/${row.bid_id}`, { status, notes: status === 'excluded' ? 'Excluded after administrator review' : 'Cleared after administrator review' });
      await loadAdminData();
      toast.success(status === 'excluded' ? 'Flagged quotation excluded' : 'Flag cleared');
    } catch (error) {
      toast.error('Failed to update flag');
    }
  };

  const openCalculationDetails = async (companyId) => {
    try {
      const response = await api.get(`/api/admin/leaderboard/details/${companyId}`, { params: { category, period } });
      setDetails(response.data);
      setShowDetails(true);
    } catch (error) {
      toast.error('Failed to load calculation details');
    }
  };

  if (loading) {
    return <div className="flex h-64 items-center justify-center text-slate-500"><RefreshCw className="mr-2 animate-spin" size={20} /> Loading leaderboard controls...</div>;
  }

  if (!settings) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-3 text-center text-slate-600">
        <AlertTriangle size={26} className="text-amber-500" />
        <p>{loadError || 'Leaderboard controls are temporarily unavailable.'}</p>
        <Button variant="outline" onClick={loadAdminData}><RefreshCw size={16} className="mr-2" />Retry</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-leaderboard">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Member Leaderboard</h1>
          <p className="mt-1 text-slate-500">Review rankings, calculation controls, and suspicious activity.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportCsv}><Download size={16} className="mr-2" />Export CSV</Button>
          {canEdit && <Button onClick={recalculate} disabled={working}><RefreshCw size={16} className={`mr-2 ${working ? 'animate-spin' : ''}`} />Recalculate</Button>}
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="rankings">Rankings</TabsTrigger>
          <TabsTrigger value="controls">Controls</TabsTrigger>
          <TabsTrigger value="flags">Flagged Activity ({flags.filter((item) => item.status === 'open').length})</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
        </TabsList>

        <TabsContent value="rankings" className="space-y-4">
          <div className="flex flex-wrap gap-2 border-y border-slate-200 py-4">
            {categories.map((item) => <Button key={item.id} size="sm" variant={category === item.id ? 'default' : 'outline'} onClick={() => setCategory(item.id)}>{item.label}</Button>)}
            <span className="mx-2 h-9 border-l border-slate-200" />
            {periods.map((item) => <Button key={item.id} size="sm" variant={period === item.id ? 'secondary' : 'ghost'} onClick={() => setPeriod(item.id)}>{item.label}</Button>)}
          </div>
          <div className="overflow-hidden rounded-md border border-slate-200 bg-white">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[850px] text-left text-sm">
                <thead className="border-b bg-slate-50 text-xs uppercase text-slate-500"><tr><th className="px-4 py-3">Rank</th><th className="px-4 py-3">Company</th><th className="px-4 py-3">Country</th><th className="px-4 py-3">Tier</th><th className="px-4 py-3">{selectedCategory?.label}</th><th className="px-4 py-3">Quotations</th><th className="px-4 py-3 text-right">Details</th></tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {(leaderboard?.results || []).map((row) => (
                    <tr key={row.company_id} className="hover:bg-slate-50"><td className="px-4 py-3 font-semibold">{row.rank ? `#${row.rank}` : 'N/A'}</td><td className="px-4 py-3 font-medium text-slate-900">{row.company_name}</td><td className="px-4 py-3 text-slate-600">{row.country || 'N/A'}</td><td className="px-4 py-3"><Badge variant="outline">{row.tier}</Badge></td><td className="px-4 py-3 font-semibold">{metric(row)}</td><td className="px-4 py-3">{row.eligible_quotations}</td><td className="px-4 py-3 text-right"><Button variant="outline" size="sm" onClick={() => openCalculationDetails(row.company_id)} title="View calculation inputs"><Calculator size={15} /></Button></td></tr>
                  ))}
                  {(leaderboard?.results || []).length === 0 && <tr><td colSpan={7} className="px-4 py-16 text-center text-slate-500">No eligible quotation activity in this period.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="controls" className="space-y-6">
          <section className="rounded-md border border-slate-200 bg-white p-5">
            <div className="mb-5 flex items-center justify-between"><div><h2 className="font-semibold text-slate-900">Calculation Settings</h2><p className="text-sm text-slate-500">Changes apply to the next refresh and are audit logged.</p></div>{canEdit && <Button onClick={saveSettings} disabled={working}><Save size={16} className="mr-2" />Save</Button>}</div>
            <div className="grid gap-5 md:grid-cols-3">
              <div><Label>Standard currency</Label><select className="mt-1 h-10 w-full rounded-md border px-3" value={settings.standard_currency} disabled={!canEdit} onChange={(event) => setSettings({ ...settings, standard_currency: event.target.value })}>{['USD', 'EUR', 'GBP', 'INR', 'AED', 'SGD', 'CNY'].map((code) => <option key={code}>{code}</option>)}</select></div>
              <div><Label>Platform timezone</Label><Input className="mt-1" value={settings.platform_timezone} disabled={!canEdit} onChange={(event) => setSettings({ ...settings, platform_timezone: event.target.value })} /></div>
              <div><Label>Refresh interval (minutes)</Label><Input type="number" min={1} className="mt-1" value={settings.refresh_minutes} disabled={!canEdit} onChange={(event) => setSettings({ ...settings, refresh_minutes: Number(event.target.value) })} /></div>
            </div>
            <div className="mt-5 grid gap-4 md:grid-cols-3">
              {categories.map((item) => <label key={item.id} className="flex items-center gap-3 rounded-md border border-slate-200 px-4 py-3 text-sm"><input type="checkbox" checked={settings.enabled_categories?.[item.id] !== false} disabled={!canEdit} onChange={(event) => setSettings({ ...settings, enabled_categories: { ...settings.enabled_categories, [item.id]: event.target.checked } })} />{item.label}</label>)}
            </div>
            <div className="mt-5 grid gap-4 md:grid-cols-4">
              {periods.map((item) => <div key={item.id}><Label>{item.label} response minimum</Label><Input type="number" min={0} className="mt-1" value={settings.minimum_quotations?.[item.id] ?? 0} disabled={!canEdit} onChange={(event) => setSettings({ ...settings, minimum_quotations: { ...settings.minimum_quotations, [item.id]: Number(event.target.value) } })} /></div>)}
            </div>
          </section>

          <div className="grid gap-6 xl:grid-cols-2">
            <section className="rounded-md border border-slate-200 bg-white p-5">
              <h2 className="font-semibold text-slate-900">Exclude Inquiry or Quotation</h2>
              <div className="mt-4 grid gap-3 sm:grid-cols-3"><select className="h-10 rounded-md border px-3" value={exclusionForm.record_type} onChange={(event) => setExclusionForm({ ...exclusionForm, record_type: event.target.value })}><option value="inquiry">Inquiry</option><option value="quotation">Quotation</option></select><Input placeholder="Record ID" value={exclusionForm.record_id} onChange={(event) => setExclusionForm({ ...exclusionForm, record_id: event.target.value })} /><Input placeholder="Reason" value={exclusionForm.reason} onChange={(event) => setExclusionForm({ ...exclusionForm, reason: event.target.value })} /></div>
              {canEdit && <Button className="mt-3" onClick={addExclusion} disabled={!exclusionForm.record_id || exclusionForm.reason.length < 3 || working}>Exclude</Button>}
              <div className="mt-4 max-h-56 overflow-auto divide-y">{exclusions.filter((item) => item.active !== false).map((row) => <div key={`${row.record_type}-${row.record_id}`} className="flex items-center justify-between gap-3 py-2 text-sm"><div><span className="font-medium">{row.record_id}</span><p className="text-xs text-slate-500">{row.record_type}: {row.reason}</p></div>{canEdit && <Button variant="ghost" size="sm" onClick={() => restoreExclusion(row)}>Restore</Button>}</div>)}</div>
            </section>
            <section className="rounded-md border border-slate-200 bg-white p-5">
              <h2 className="font-semibold text-slate-900">Company Eligibility</h2>
              <div className="mt-4 grid gap-3 sm:grid-cols-2"><Input placeholder="Company ID" value={suspensionForm.company_id} onChange={(event) => setSuspensionForm({ ...suspensionForm, company_id: event.target.value })} /><Input placeholder="Reason" value={suspensionForm.reason} onChange={(event) => setSuspensionForm({ ...suspensionForm, reason: event.target.value })} /></div>
              {canEdit && <Button variant="destructive" className="mt-3" onClick={suspendCompany} disabled={!suspensionForm.company_id || suspensionForm.reason.length < 3 || working}><ShieldBan size={16} className="mr-2" />Suspend</Button>}
              <div className="mt-4 max-h-56 overflow-auto divide-y">{suspensions.filter((item) => item.active).map((row) => <div key={row.company_id} className="flex items-center justify-between gap-3 py-2 text-sm"><div><span className="font-medium">{row.company_id}</span><p className="text-xs text-slate-500">{row.reason}</p></div>{canEdit && <Button variant="ghost" size="sm" onClick={() => restoreCompany(row)}>Restore</Button>}</div>)}</div>
            </section>
          </div>
        </TabsContent>

        <TabsContent value="flags">
          <section className="overflow-hidden rounded-md border border-slate-200 bg-white">
            <table className="w-full text-left text-sm"><thead className="border-b bg-slate-50 text-xs uppercase text-slate-500"><tr><th className="px-4 py-3">Reason</th><th className="px-4 py-3">Company</th><th className="px-4 py-3">Quotation</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Review</th></tr></thead><tbody className="divide-y">{flags.map((row, index) => <tr key={`${row.bid_id || row.company_id}-${index}`}><td className="px-4 py-3"><span className="inline-flex items-center"><AlertTriangle size={15} className="mr-2 text-amber-600" />{row.reason}</span></td><td className="px-4 py-3">{row.company_id}</td><td className="px-4 py-3">{row.bid_id || 'Multiple'}</td><td className="px-4 py-3"><Badge variant="outline">{row.status}</Badge></td><td className="px-4 py-3 text-right">{canEdit && row.status === 'open' && row.bid_id && <div className="flex justify-end gap-2"><Button size="sm" variant="outline" onClick={() => reviewFlag(row, 'cleared')}>Clear</Button><Button size="sm" variant="destructive" onClick={() => reviewFlag(row, 'excluded')}>Exclude</Button></div>}</td></tr>)}{flags.length === 0 && <tr><td colSpan={5} className="px-4 py-16 text-center text-slate-500">No flagged activity.</td></tr>}</tbody></table>
          </section>
        </TabsContent>

        <TabsContent value="history">
          <section className="overflow-hidden rounded-md border border-slate-200 bg-white"><table className="w-full text-left text-sm"><thead className="border-b bg-slate-50 text-xs uppercase text-slate-500"><tr><th className="px-4 py-3">Period</th><th className="px-4 py-3">Category</th><th className="px-4 py-3">Period Key</th><th className="px-4 py-3">Companies</th><th className="px-4 py-3">Updated</th></tr></thead><tbody className="divide-y">{history.map((row) => <tr key={`${row.category}-${row.period}-${row.period_key}`}><td className="px-4 py-3">{row.period}</td><td className="px-4 py-3">{row.category}</td><td className="px-4 py-3">{row.period_key}</td><td className="px-4 py-3">{row.results?.length || 0}</td><td className="px-4 py-3">{new Date(row.updated_at).toLocaleString()}</td></tr>)}</tbody></table></section>
        </TabsContent>
      </Tabs>

      <Dialog open={showDetails} onOpenChange={setShowDetails}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Calculation Details: {details?.company_id}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">Stored ranking inputs only. Customer and private inquiry details are not shown.</p>
          <div className="overflow-x-auto rounded-md border"><table className="w-full min-w-[760px] text-left text-xs"><thead className="border-b bg-slate-50"><tr><th className="px-3 py-2">Quotation ID</th><th className="px-3 py-2">Submitted</th><th className="px-3 py-2">Standard Value</th><th className="px-3 py-2">Weight kg</th><th className="px-3 py-2">Response seconds</th></tr></thead><tbody className="divide-y">{(details?.records || []).map((row) => <tr key={row.bid_id}><td className="px-3 py-2">{row.bid_id}</td><td className="px-3 py-2">{new Date(row.created_at).toLocaleString()}</td><td className="px-3 py-2">{row.leaderboard_value?.standard_currency} {row.leaderboard_value?.standard_amount ?? 'N/A'}</td><td className="px-3 py-2">{row.leaderboard_weight_kg ?? 'N/A'}</td><td className="px-3 py-2">{row.leaderboard_response_seconds ?? 'N/A'}</td></tr>)}</tbody></table></div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminLeaderboard;
